import { useLocation, useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { ConnectorRouteView } from '@/features/integrations';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import CustomerioConnectorPage from './index.page';

const LOADING_LABEL = 'Loading integration…';

/**
 * The migrated URL drops the `journeys` segment; the legacy one is what the embedded Ember app
 * serves.
 */
function useHandoffHref(side: 'migrated' | 'legacy'): string | null {
  const router = useRouter();
  const { pathname, searchStr, hash } = useLocation();
  const workspaceId = getWorkspaceIdFromUrl(pathname);

  if (workspaceId == null) {
    return null;
  }

  const to =
    side === 'migrated'
      ? '/workspaces/$workspaceId/integrations/customerio'
      : '/workspaces/$workspaceId/journeys/integrations/customerio';

  const href = router.buildLocation({ to, params: { workspaceId } }).href;
  // Ember transitions preserve query params, and the page deep-links to its snippet panel by
  // fragment, so both directions carry the query string and the hash.
  return `${href}${searchStr}${hash === '' ? '' : `#${hash}`}`;
}

/**
 * The old `/journeys/integrations/customerio` URL. Flag off → the embedded Ember app still owns the
 * page and renders here. Flag on → Hydra has claimed the URL only to forward it to the migrated
 * route, so the directory cards and the activity-log deep link keep working without being
 * re-pointed.
 */
export function JourneysCustomerioHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.IntegrationConnectorsMigration);
  const to = useHandoffHref('migrated');

  if (!isMigrated) {
    return <Journeys />;
  }

  // `/workspaces/last` is rewritten by a bridge-synced navigation that leaves route params stale,
  // so the id is read off the reactive location and held until it lands.
  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * The migrated `/integrations/customerio` URL.
 *
 * `ConnectorRouteView` applies the gates Ember's `integrations` root route applied. This page's own
 * `beforeModel` added `account/api_credentials/view` on top, so that gate nests inside — and the
 * order matters: a user missing both lands where Ember's parent redirect sent them.
 */
export function CustomerioConnectorRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.IntegrationConnectorsMigration);
  const legacyHref = useHandoffHref('legacy');

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacyHref}>
      {(workspaceId) => (
        // Ember's own redirect resolved to this page's parent route, the integrations index.
        <ProtectedRoute
          permission="account/api_credentials/view"
          redirectTo={generatePath('/workspaces/:workspaceId/integrations', { workspaceId })}
        >
          <CustomerioConnectorPage />
        </ProtectedRoute>
      )}
    </ConnectorRouteView>
  );
}
